import { useEffect, useState } from 'react';
import { loadSourceBoundScenario } from '../consumer/source-bound-scenario';
import type { ScenarioObservation } from '../domain/types';
import {
  defaultIssueCode,
  findIssue,
  findScenarioCard,
  formedScenarioId,
} from './scenario-center-content';
import { ScenarioCenterHeader } from './ScenarioCenterHeader';
import { ScenarioDetailView, type DetailFocus, type DetailTab } from './ScenarioDetailView';
import { ScenarioLibraryView, type LibraryFilters } from './ScenarioLibraryView';
import { ScenarioMaintenancePanel } from './ScenarioMaintenancePanel';
import { ScenarioReadState } from './ScenarioReadState';
import { ForestScenarioDetailView } from './ForestScenarioDetailView';
import { ChemicalScenarioDetailView } from './ChemicalScenarioDetailView';
import { TransportScenarioDetailView } from './TransportScenarioDetailView';
import { FloodScenarioDetailView } from './FloodScenarioDetailView';
import { defaultForestIssue, findForestIssue } from './forest-research-content';
import { defaultChemicalIssue, findChemicalIssue } from './chemical-research-content';
import { defaultTransportIssue, findTransportIssue } from './transport-research-content';
import { defaultFloodIssue, findFloodIssue } from './flood-research-content';
import './scenario-center.css';

/**
 * The Scenario Center route.
 *
 * TWO PAGES, NOT SIX VIEWS. The library and the scene detail are the whole business surface. The
 * old `view` parameter is still READ so that a bookmarked or historical link lands on the reading
 * section it named instead of a blank page, but it no longer selects a parallel navigation.
 *
 * WHAT STILL READS REAL DATA. The projection still comes from the fixed source-bound
 * ScenarioTemplate through the same reader, the same mapping module and the same exact-pin
 * records. Nothing on this page writes, creates a mission, or asserts a capability, an asset or
 * an authorization.
 */
function currentQuery() {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/**
 * The maintenance and source view is a CONTROLLED DEVELOPMENT ENTRY, not part of the business
 * page.
 *
 * A rescue reader must not be handed the six-field pins, the raw source rows and the lifecycle
 * enums — and "folded behind a <details>" is not the same as absent: it is still in the DOM, in
 * the accessibility tree and one keystroke away. So it is not rendered at all unless this is a
 * development build, on the loopback interface, with an explicit `maintenance=1`.
 *
 * The maintenance component is kept, not deleted: the repository's source fidelity, Unknown and
 * mission-template contract tests still exercise it, and content maintainers still need it. This
 * gate is the same shape the platform already uses for its other synthetic development surfaces.
 */
function maintenanceEnabled(): boolean {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return false;
  const host = window.location.hostname;
  if (!['127.0.0.1', 'localhost', '::1', '[::1]'].includes(host)) return false;
  return new URLSearchParams(window.location.search).get('maintenance') === '1';
}

const legacyViews = ['assessment', 'information', 'capability', 'machine', 'templates', 'sop'];

/** Where a historical `view` link now lands. */
function resolveLegacyView(view: string | null): {
  readonly tab: DetailTab;
  readonly focus: DetailFocus;
  readonly notice: string | null;
} {
  if (view === 'information') return { tab: 'issues', focus: 'information', notice: null };
  if (view === 'capability' || view === 'machine')
    return { tab: 'issues', focus: 'capability', notice: null };
  if (view === 'templates')
    return {
      tab: 'evidence',
      focus: null,
      notice:
        '原“任务模板”主入口已撤下：任务模板的阶段编号与授权字段不是消防人员需要阅读的业务内容。后台模板定义与版本关联保持不变。',
    };
  if (view === 'sop')
    return {
      tab: 'evidence',
      focus: null,
      notice: '原“本地 SOP”主入口已撤下：本地规程尚未取得机构 mandate，不能作为本地预案展示。',
    };
  return { tab: 'issues', focus: null, notice: null };
}

export function ScenarioCenterPage({
  initialObservation,
  developmentLoopbackOrigin,
}: {
  readonly initialObservation?: ScenarioObservation;
  readonly developmentLoopbackOrigin?: string;
}) {
  const [observation, setObservation] = useState<ScenarioObservation>(
    initialObservation ?? { status: 'LOADING', message: '正在读取固定来源绑定的场景模板' },
  );
  const [query, setQuery] = useState(currentQuery);
  useEffect(() => {
    if (initialObservation) return;
    let mounted = true;
    void Promise.resolve().then(() => {
      if (mounted) setObservation(loadSourceBoundScenario());
    });
    return () => {
      mounted = false;
    };
  }, [initialObservation]);
  useEffect(() => {
    const change = () => setQuery(window.location.search);
    window.addEventListener('popstate', change);
    return () => window.removeEventListener('popstate', change);
  }, []);
  const params = new URLSearchParams(query);
  const scene = params.get('scene');
  const rawView = params.get('view');
  const legacy = resolveLegacyView(legacyViews.includes(rawView ?? '') ? rawView : null);
  const tab: DetailTab = params.get('tab') === 'evidence' ? 'evidence' : legacy.tab;
  const filters: LibraryFilters = {
    query: params.get('q') ?? '',
    category: params.get('category') ?? '',
  };
  /**
   * An unknown `issue` is normalised to the default one HERE, not inside the detail view.
   *
   * Left alone, the detail view fell back to the first issue for its title while still passing
   * the unknown code to `linkedEntries`, which then returned an UNKNOWN notice — a page whose
   * heading said 烟气蔓延 and whose body said the issue could not be resolved. One normalisation
   * point keeps the heading, the information set and the capability set describing one issue.
   */
  const forest = scene === 'forest-fire';
  const chemical = scene === 'chemical';
  const transport = scene === 'transport';
  const flood = scene === 'flood';
  const defaultCode = forest
    ? defaultForestIssue
    : chemical
      ? defaultChemicalIssue
      : transport
        ? defaultTransportIssue
        : flood
          ? defaultFloodIssue
          : defaultIssueCode;
  const validIssue = forest
    ? findForestIssue
    : chemical
      ? findChemicalIssue
      : transport
        ? findTransportIssue
        : flood
          ? findFloodIssue
          : findIssue;
  const requestedIssue = params.get('issue') ?? defaultCode;
  const issue = validIssue(requestedIssue) ? requestedIssue : defaultCode;
  /**
   * The normalisation above keeps the PAGE consistent; this keeps the ADDRESS consistent with it.
   * Without it a shared link kept `?issue=NOT-A-REAL-ISSUE` while the page showed 烟气蔓延, and
   * the next reader was sent back to a code that resolves to nothing. A replaceState rather than a
   * pushState: correcting the URL is not a navigation the reader asked for, so Back still leaves
   * the page.
   */
  useEffect(() => {
    const live = new URLSearchParams(window.location.search).get('issue');
    if (
      !live ||
      (forest
        ? findForestIssue(live)
        : chemical
          ? findChemicalIssue(live)
          : transport
            ? findTransportIssue(live)
            : flood
              ? findFloodIssue(live)
              : findIssue(live))
    )
      return;
    const corrected = new URLSearchParams(window.location.search);
    corrected.set('issue', defaultCode);
    // `location.hash` is preserved: the platform's skip link targets one, and a URL correction is
    // not a reason to drop it.
    window.history.replaceState(
      null,
      '',
      window.location.pathname + '?' + corrected.toString() + window.location.hash,
    );
    setQuery(window.location.search);
  }, [query, issue, forest, chemical, transport, flood, defaultCode]);
  function navigate(changes: Readonly<Record<string, string | null>>, replace = false) {
    const next = new URLSearchParams(query);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    const search = next.size ? '?' + next.toString() : '';
    if (typeof window !== 'undefined') {
      window.history[replace ? 'replaceState' : 'pushState'](
        null,
        '',
        window.location.pathname + search,
      );
      setQuery(search);
    }
  }
  const data = observation.status === 'AVAILABLE' ? observation.data : null;
  const sceneCard = scene ? findScenarioCard(scene) : undefined;
  const inLibrary = !sceneCard || sceneCard.state !== 'REFERENCE';
  const maintenance = maintenanceEnabled();
  return (
    <main className="scc-page">
      <ScenarioCenterHeader variant={inLibrary ? 'library' : 'detail'} />
      <div className="scc-workspace" id="scc-content" tabIndex={-1}>
        {forest ? (
          <ForestScenarioDetailView
            location={{ issue, tab, focus: legacy.focus }}
            onLocation={(next) =>
              navigate({ scene: 'forest-fire', issue: next.issue, tab: next.tab, view: null })
            }
            onLibrary={() => navigate({ scene: null, issue: null, tab: null, view: null })}
          />
        ) : chemical ? (
          <ChemicalScenarioDetailView
            location={{ issue, tab, focus: legacy.focus }}
            onLocation={(next) =>
              navigate({ scene: 'chemical', issue: next.issue, tab: next.tab, view: null })
            }
            onLibrary={() => navigate({ scene: null, issue: null, tab: null, view: null })}
          />
        ) : transport ? (
          <TransportScenarioDetailView
            location={{ issue, tab, focus: legacy.focus }}
            onLocation={(next) =>
              navigate({ scene: 'transport', issue: next.issue, tab: next.tab, view: null })
            }
            onLibrary={() => navigate({ scene: null, issue: null, tab: null, view: null })}
          />
        ) : flood ? (
          <FloodScenarioDetailView
            location={{ issue, tab, focus: legacy.focus }}
            onLocation={(next) =>
              navigate({ scene: 'flood', issue: next.issue, tab: next.tab, view: null })
            }
            onLibrary={() => navigate({ scene: null, issue: null, tab: null, view: null })}
          />
        ) : !data ? (
          <ScenarioReadState
            status={observation.status === 'AVAILABLE' ? 'ERROR' : observation.status}
            message={observation.status === 'AVAILABLE' ? '来源投影不可用' : observation.message}
          />
        ) : inLibrary ? (
          <ScenarioLibraryView
            data={data}
            filters={filters}
            initialScope={sceneCard?.state === 'IN_PREPARATION' ? sceneCard.id : null}
            onFilters={(next) => navigate({ q: next.query, category: next.category }, true)}
            onEnter={(scenarioId) =>
              navigate({
                scene: scenarioId,
                tab: 'issues',
                issue:
                  scenarioId === 'forest-fire'
                    ? defaultForestIssue
                    : scenarioId === 'chemical'
                      ? defaultChemicalIssue
                      : scenarioId === 'transport'
                        ? defaultTransportIssue
                        : scenarioId === 'flood'
                          ? defaultFloodIssue
                          : defaultIssueCode,
                view: null,
              })
            }
          />
        ) : (
          <>
            {legacy.notice && (
              <p className="scc-legacy-notice" role="status">
                {legacy.notice}
              </p>
            )}
            <ScenarioDetailView
              data={data}
              location={{ issue, tab, focus: legacy.focus }}
              onLocation={(next) =>
                navigate({
                  scene: formedScenarioId,
                  tab: next.tab,
                  issue: next.issue,
                  view: null,
                })
              }
              onLibrary={() => navigate({ scene: null, tab: null, issue: null, view: null })}
            />
          </>
        )}
        {data && maintenance && (
          <ScenarioMaintenancePanel
            data={data}
            {...(developmentLoopbackOrigin ? { developmentLoopbackOrigin } : {})}
          />
        )}
      </div>
      <p className="scc-safety-line">场景资料用于研究与准备 · 不代表现场事实或行动授权</p>
    </main>
  );
}
